/**
 * Display-unit preferences, and the conversion and formatting that follow
 * from them.
 *
 * Every number already stored — `TrackPoints.altFt`, `.gsKt`, a distance
 * computed by `haversineNm` — is in the unit `lib/parse/units.ts` converts
 * *into* at import time: feet, knots, nautical miles. Those stay canonical
 * regardless of what a pilot prefers to read, the same way an instant stays
 * UTC in storage regardless of what zone it displays in. This is the layer
 * that turns a canonical number into the unit the Settings page asked for,
 * and it is the only place that conversion happens — the same discipline
 * `format.ts` already keeps for time.
 */

import { feetToMetres, METRES_PER_NAUTICAL_MILE } from './parse';

export type AltitudeUnit = 'ft' | 'm';
export type SpeedUnit = 'kt' | 'kmh';
export type DistanceUnit = 'nm' | 'km';

export interface UnitPreferences {
  altitude: AltitudeUnit;
  speed: SpeedUnit;
  distance: DistanceUnit;
}

/** Feet, knots, nautical miles — what every FR24 export and EFB log already reads in. */
export const DEFAULT_UNIT_PREFERENCES: UnitPreferences = {
  altitude: 'ft',
  speed: 'kt',
  distance: 'nm',
};

/** A knot and a km/h share this factor with a nautical mile and a km — both are "distance per hour". */
const KM_PER_NM = METRES_PER_NAUTICAL_MILE / 1000;

export function convertAltitude(valueFt: number, unit: AltitudeUnit): number {
  return unit === 'm' ? feetToMetres(valueFt) : valueFt;
}

export function convertSpeed(valueKt: number, unit: SpeedUnit): number {
  return unit === 'kmh' ? valueKt * KM_PER_NM : valueKt;
}

export function convertDistance(valueNm: number, unit: DistanceUnit): number {
  return unit === 'km' ? valueNm * KM_PER_NM : valueNm;
}

export const ALTITUDE_UNIT_LABEL: Record<AltitudeUnit, string> = { ft: 'ft', m: 'm' };
export const SPEED_UNIT_LABEL: Record<SpeedUnit, string> = { kt: 'kt', kmh: 'km/h' };
export const DISTANCE_UNIT_LABEL: Record<DistanceUnit, string> = { nm: 'nm', km: 'km' };

function formatWhole(value: number, unit: string): string {
  return Number.isFinite(value) ? `${Math.round(value).toLocaleString('en-NZ')} ${unit}` : '—';
}

export function formatAltitude(valueFt: number, unit: AltitudeUnit): string {
  return formatWhole(convertAltitude(valueFt, unit), ALTITUDE_UNIT_LABEL[unit]);
}

export function formatSpeed(valueKt: number, unit: SpeedUnit): string {
  return formatWhole(convertSpeed(valueKt, unit), SPEED_UNIT_LABEL[unit]);
}

export function formatDistance(valueNm: number, unit: DistanceUnit): string {
  return formatWhole(convertDistance(valueNm, unit), DISTANCE_UNIT_LABEL[unit]);
}
